
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import LogTable from "@/components/logs/LogTable.vue";
import KeyRelays from "@/components/keys/KeyRelays.vue";
import Timeout = NodeJS.Timeout;
import { concatListElem } from "@/utils/misc";

const DEBOUNCE_TIME_MILLI: number = 500;

@Component({
  components: { KeyRelays, LogTable },
  methods: { concatListElem },
})
export default class KeyRelaysTable extends Vue {

  get keyReferenceRelay(): any {
    return this.currentKey?.Relays?.find((relay) => relay.type === "AGENCY") || this.currentKey?.Relays?.[0];
  }
  get isArchived(): boolean {
    return !!this.currentKey?.archivedAt;
  }
  get isCompanyBlocked(): boolean {
    return !!this.currentKey?.company?.blockedAt;
  }
  get currentBuildingId(): number|undefined {
    return this.currentKey?.currentLocation?.Box?.buildingId;
  }
  get isEditBtnDisabled(): boolean {
    return this.isCompanyBlocked
      || this.isArchived
      || this.currentKey.status === "IN_RELAY"
      || this.currentKey.status === "IN_TRANSIT"
      || !this.hasRight("EDIT_KEY_RELAY");
  }

  @Prop({default: null})
  public currentKey: any;
  @Getter private canDoInEnvCompany: (right: string) => boolean;
  @Getter private canDoCategory: (category: string, companyId: number) => boolean;

  private relayId: number = null;
  private editRelayDialog: boolean = false;
  private loadingSaveRelay: boolean = false;
  private searchAllRelays: string = null;
  private agencyRelays: any[] = [];
  private loadingGetRelays: boolean = false;
  private timerId: Timeout = null;

  @Watch("searchAllRelays", {deep: true})
  public handlerSearchRelays(newVal: string): void {
    this.searchRelays();
  }

  private mounted(): void {
    this.reset();
    this.$nextTick().then(() => {
      this.$emit("loaded");
    });
  }

  private reset(): void {
    this.closeReplaceReferenceRelay();
    this.searchAllRelays = null;
  }

  private async getRelays(): Promise<void> {
    await this.$store.dispatch("companies/getRelays", {
      companyId: this.currentKey.companyId,
      query: {
        search: this.searchAllRelays,
        itemsPerPage: 10,
      },
    }).then((res) => {
      this.agencyRelays = res.rows || [];
    });
  }

  private async saveRelay(): Promise<void> {
    this.loadingSaveRelay = true;
    await this.$store.dispatch("keys/editRelays", {
      keyId: this.currentKey.id,
      relayIds: [this.relayId],
    }).then((res) => {
      this.reset();
      this.$emit("reset");
      this.$store.commit("alerts/displaySuccess", {
        icon: "icon-picto_cle-partagees",
        msg: this.$i18n?.t("alerts.key.editionSuccess"),
      });
    }).finally(() => {
      this.loadingSaveRelay = false;
    });
  }

  private searchRelays(): void {
    this.loadingGetRelays = true;
    // cancel pending call
    clearTimeout(Number(this.timerId));
    // delay new call
    this.timerId = setTimeout(() => {
      this.getRelays().finally(() => {
        this.loadingGetRelays = false;
      });
    }, DEBOUNCE_TIME_MILLI);
  }

  private openReplaceReferenceRelay(): void {
    this.getRelays();
    this.editRelayDialog = true;
  }

  private closeReplaceReferenceRelay(): void {
    this.editRelayDialog = false;
    this.relayId = this.keyReferenceRelay?.id;
  }

  private hasRight(right: string): boolean {
    return this.canDoInEnvCompany(right);
  }

  private hasCategoryRight(category: string): boolean {
    if (category === "editKey") {
      const editRights = [
        "EDIT_KEY_NAME",
        "EDIT_KEY_DESCRIPTION",
        "EDIT_KEY_TAGS",
        "EDIT_KEY_IMAGE",
        "EDIT_KEY_PRIVATE_IMAGES",
        "EDIT_KEY_RELAY",
        "EDIT_KEY_RESERVED_LOCATION",
      ];
      return editRights.some((right) => this.canDoInEnvCompany(right));
    }
  }

  private itemStringified = (item) => JSON.stringify(item);
}
